/* @luxottica/vm-devicedetection v1.2.15 */
import { UAParser } from 'ua-parser-js';

class UserAgentParser {
    constructor() {
        this.userAgentData = new UAParser().getResult();
    }
    static getInstance() {
        if (!UserAgentParser.instance) {
            UserAgentParser.instance = new UserAgentParser();
        }
        return UserAgentParser.instance;
    }
    getUserAgentData() {
        return this.userAgentData;
    }
}

class WasmDetection {
    constructor() {
        this.simplewasm = Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00);
    }
    static getInstance() {
        if (!WasmDetection.instance) {
            WasmDetection.instance = new WasmDetection();
        }
        return WasmDetection.instance;
    }
    isWebAssemblySupported() {
        let supported = false;
        try {
            if (typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function") {
                const module = new WebAssembly.Module(this.simplewasm);
                supported = module instanceof WebAssembly.Module
                    && new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
            }
        }
        catch (e) {
            supported = false;
        }
        return supported;
    }
}

class WebGlDetection {
    static getInstance() {
        if (!WebGlDetection.instance) {
            WebGlDetection.instance = new WebGlDetection();
        }
        return WebGlDetection.instance;
    }
    isWebGlSupported() {
        let supported = false;
        try {
            const canvas = document.createElement("canvas");
            canvas.setAttribute("id", WebGlDetection.tmpCanvasElementId);
            if (canvas) {
                const webglContext = this.getWebglContext(canvas, ["webgl2", "webgl", "experimental-webgl"]);
                if (webglContext) {
                    supported = true;
                }
            }
        }
        catch (e) {
            supported = false;
        }
        finally {
            if (document) {
                const elementToRemove = document.getElementById(WebGlDetection.tmpCanvasElementId);
                if (elementToRemove) {
                    elementToRemove.outerHTML = "";
                }
            }
        }
        return supported;
    }
    getWebglContext(canvas, webglContext) {
        let webglRenderingContext;
        for (const contextName of webglContext) {
            webglRenderingContext = canvas.getContext(contextName, WebGlDetection.webglCtxAttributes);
            if (webglRenderingContext) {
                break;
            }
        }
        return webglRenderingContext;
    }
}
WebGlDetection.tmpCanvasElementId = "canvas-for-webgl-check";
WebGlDetection.webglCtxAttributes = { depth: true, premultipliedAlpha: false };

class WebRtcDetection {
    static getInstance() {
        if (!WebRtcDetection.instance) {
            WebRtcDetection.instance = new WebRtcDetection();
        }
        return WebRtcDetection.instance;
    }
    isUserMediaSupported() {
        return this.isMediaDeviceAvailable() || this.isNativeBridgePresent();
    }
    isVideoInputDeviceAvailable() {
        let videoInputDeviceAvailability;
        try {
            if (this.isMediaDeviceAvailable()) {
                videoInputDeviceAvailability = navigator.mediaDevices.enumerateDevices()
                    .then((devices) => devices.some((device) => device.kind === "videoinput"));
            }
            else if (this.isNativeBridgePresent()) {
                videoInputDeviceAvailability = Promise.resolve(true);
            }
            else {
                videoInputDeviceAvailability = Promise.resolve(false);
            }
        }
        catch (e) {
            videoInputDeviceAvailability = Promise.resolve(false);
        }
        return videoInputDeviceAvailability;
    }
    isMediaDeviceAvailable() {
        return (typeof navigator.mediaDevices === "object") && (typeof navigator.mediaDevices.getUserMedia === "function");
    }
    isNativeBridgePresent() {
        return (
        // TODO do we need to create a vm-nativebridge module in order to centralise this check?
        typeof window.webkit === "object" &&
            typeof (window.webkit.messageHandlers) === "object" &&
            typeof (window.webkit.messageHandlers.startNativeCam) === "object");
    }
}

class DeviceDetection {
    constructor() {
        this.uaData = UserAgentParser.getInstance().getUserAgentData();
    }
    isAppleiOS13() {
        let isIOS13 = false;
        if (this.uaData.os && this.uaData.os.name && this.uaData.os.name === "iOS") {
            if (this.uaData.os.version && this.uaData.os.version.startsWith("13")) {
                isIOS13 = true;
                // tslint:disable-next-line: no-console
                console.info("--- detected iOS 13 ---");
            }
        }
        else if (this.isiPadOS()) {
            isIOS13 = true;
            // tslint:disable-next-line: no-console
            console.info("--- detected iPad 13 ---");
            // tslint:disable-next-line: no-console
            console.warn("iPad OS detection is still a workaround, Apple does not use a specific UA for iPad");
        }
        return isIOS13;
    }
    isiOS() {
        return ((this.uaData.os && this.uaData.os.name && this.uaData.os.name === "iOS") || this.isiPadOS());
    }
    // Workaround for iPadOS which is currently not detectable via UA
    // https://github.com/faisalman/ua-parser-js/issues/387#issuecomment-579147248
    isiPadOS() {
        const osName = (this.uaData.os && this.uaData.os.name && this.uaData.os.name) || undefined;
        const iPadOS = window.orientation !== undefined && osName === "Mac OS";
        return osName === "iOS" || iPadOS;
    }
    getUserAgentData() {
        return this.uaData;
    }
    isWebView() {
        let isWebView = false;
        if (this.uaData.os && this.uaData.os.name && this.uaData.os.name === "iOS") {
            if (this.uaData.engine && this.uaData.engine.name && this.uaData.engine.name === "WebKit") {
                if (this.uaData.browser && this.uaData.browser.name && this.uaData.browser.name === "WebKit") {
                    isWebView = true;
                }
            }
        }
        return isWebView;
    }
    isWebViewBrowser() {
        let isWebViewBrowser = false;
        if (this.uaData.os && this.uaData.os.name && this.uaData.os.name === "iOS") {
            if (this.uaData.engine && this.uaData.engine.name && this.uaData.engine.name === "WebKit") {
                if (this.uaData.browser && this.uaData.browser.name && this.uaData.browser.name !== "Mobile Safari") {
                    isWebViewBrowser = true;
                }
            }
        }
        return isWebViewBrowser;
    }
    isWebAssemblySupported() {
        return WasmDetection.getInstance().isWebAssemblySupported();
    }
    isUserMediaSupported() {
        return WebRtcDetection.getInstance().isUserMediaSupported();
    }
    isVideoInputDeviceAvailable() {
        return WebRtcDetection.getInstance().isVideoInputDeviceAvailable();
    }
    isWebGlSupported() {
        return WebGlDetection.getInstance().isWebGlSupported();
    }
}

export { DeviceDetection };
//# sourceMappingURL=vm-devicedetection.js.map
